import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Award } from 'lucide-react';
import clsx from 'clsx';
import Badge from '@components/common/Badge';
import Button from '@components/common/Button';
import { StatusIcon } from '@components/common';
import { StatusCell } from '@components/common/table';
import { Assessment, ROLES } from '@app-types';
import { hasRole } from '@utils/permissionUtils';
import { useAuth } from '@contexts/AuthContext';
import AssessmentInfo from './AssessmentInfo';
import { hasOpenConflicts, getTierConflictTooltip } from '@utils/conflictUtils';
import { statusToVariant } from '@utils/badgeMappers';
import styles from './AssessmentStatusOverview.module.css';

interface ProgressData {
  percentage: number;
  evaluated: number;
  total: number;
  implemented: number;
  implementedNeedsEvidence: number;
  gaps: number;
  notApplicable: number;
  notAssessed: number;
  remediated: number;
  compliant: number;
}

interface AssessmentStatusOverviewProps {
  assessment: Assessment;
  progress: ProgressData;
  canReassign?: boolean;
  onAssign?: () => void;
  onComplete?: () => void;
  canComplete?: boolean;
}

const AssessmentStatusOverview: React.FC<AssessmentStatusOverviewProps> = ({
  assessment,
  progress,
  canReassign = false,
  onAssign,
  onComplete,
  canComplete = false
}) => {
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate completion status and remaining items
  const isFullyEvaluated = progress.notAssessed === 0;
  const isPostPublishOrTerminal = [
    'Pending Evidence',
    'Evidence Past Due',
    'Escalated',
    'Final',
    'Cancelled',
    'Expired'
  ].includes(assessment.assessment_status);
  const isAlreadyCompleted = isPostPublishOrTerminal;
  const canShowCompleteButton = hasRole(user, [ROLES.ASSESSOR, ROLES.ADMIN]) && !isPostPublishOrTerminal;

  // Button state logic
  const completeButtonDisabled = !isFullyEvaluated || isAlreadyCompleted || !canComplete;

  // Helper text for the complete button
  const getCompleteButtonText = () => {
    if (assessment.assessment_status === 'Final') return 'Assessment Finalized';
    if (['Pending Evidence', 'Evidence Past Due', 'Escalated'].includes(assessment.assessment_status)) {
      return 'Assessment Submitted';
    }
    if (!isFullyEvaluated) return `Publish Assessment (${progress.notAssessed} items remaining)`;
    return 'Publish Assessment';
  };

  return (
    <div className={styles.statusOverviewContainer} data-test-id="status-overview-container">
      {/* Compact Status Overview */}
      <div className={styles.statusOverview} data-test-id="status-overview">
        <div className={styles.statusCard}>
          <div className={styles.statusIcon}>
            <StatusIcon status="pending" size={24} />
          </div>
          <div className={styles.statusContent}>
            <div className={styles.statusLabel}>Status</div>
            <StatusCell
              hasConflict={hasOpenConflicts(assessment)}
              conflictTooltip={hasOpenConflicts(assessment) ? getTierConflictTooltip(assessment) : undefined}
            >
              <Badge variant={statusToVariant(assessment.assessment_status)}>{assessment.assessment_status}</Badge>
            </StatusCell>
          </div>
        </div>

        <div className={styles.statusCard}>
          <div className={styles.statusIcon}>
            <StatusIcon status="success" size={24} />
          </div>
          <div className={styles.statusContent}>
            <div className={styles.statusLabel}>Progress</div>
            <div className={styles.progressInfo}>
              <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${progress.percentage}%` }}></div>
              </div>
              <span className={styles.progressText}>
                {progress.percentage}% Complete ({progress.compliant}/{progress.total} items)
              </span>
            </div>
          </div>
        </div>

        <div className={styles.statusCard}>
          <div className={styles.statusIcon}>
            <StatusIcon status="error" size={24} />
          </div>
          <div className={styles.statusContent}>
            <div className={styles.statusLabel}>Total Gaps</div>
            <div className={styles.gapSummary}>
              <span className={styles.gapTotal}>{assessment.total_gap_count}</span>
              <div className={styles.gapBreakdown}>
                <span className={styles.gapHigh}>H: {assessment.high_gaps}</span>
                <span className={styles.gapMedium}>M: {assessment.medium_gaps}</span>
                <span className={styles.gapLow}>L: {assessment.low_gaps}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Complete Assessment Button */}
        {canShowCompleteButton && (
          <div
            className={clsx(
              styles.statusCard,
              styles.completeAssessmentCard,
              isAlreadyCompleted && styles.assessmentCompleted,
              isFullyEvaluated && !isAlreadyCompleted && styles.assessmentReady
            )}
          >
            <div className={styles.statusIcon}>
              <Award
                size={24}
                style={{
                  color: (() => {
                    if (isAlreadyCompleted) return 'var(--color-gray-500)';
                    if (isFullyEvaluated) return 'var(--color-success-600)';
                    return 'var(--color-primary-600)';
                  })()
                }}
              />
            </div>
            <div className={styles.statusContent}>
              <Button
                variant={isAlreadyCompleted ? 'ghost' : 'primary'}
                size="sm"
                onClick={onComplete}
                disabled={completeButtonDisabled}
              >
                <Award size={16} />
                {getCompleteButtonText()}
              </Button>
              {!isFullyEvaluated && !isAlreadyCompleted && (
                <div className={styles.completionHint}>Evaluate all items to publish</div>
              )}
            </div>
          </div>
        )}

        {/* Expand/Collapse Toggle */}
        <div className={styles.statusToggle}>
          <Button variant="ghost" size="sm" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? (
              <>
                <ChevronUp size={16} />
                Less
              </>
            ) : (
              <>
                <ChevronDown size={16} />
                Details
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Expanded Detailed Information */}
      {isExpanded && (
        <div className={styles.statusDetails} data-test-id="status-details">
          <AssessmentInfo assessment={assessment} canReassign={canReassign} onAssign={onAssign} showContractInfo={true} />
        </div>
      )}
    </div>
  );
};

export default AssessmentStatusOverview;
